// Copyright (c) Ping. All rights reserved.
//
// Per-conversation composer draft cache. Persists the in-progress
// message (the markdown buffer, so formatting survives) keyed by a
// stable conversation id, so reloading the browser — or switching away
// and back — never loses typing progress. The draft is written on every
// change and removed the moment the composer is emptied or the message
// is sent.
//
// Drafts carry a timestamp and expire after `DRAFT_MAX_AGE_MS` (7 days),
// mirroring the Element/Matrix lexical composer's `draftStorage`. Stale
// entries are pruned opportunistically (on every load/save) so a user
// who abandons dozens of conversations doesn't accumulate unbounded
// localStorage — there's no background sweeper on RN-web.
//
// Backed by `localStorage` on web; a no-op on platforms without it
// (react-native-macos), where the composer simply behaves as before.
// All access is wrapped so a disabled/again full quota never throws into
// the render path.

const PREFIX = 'ping.composer.draft.';

/** Drafts older than this are treated as absent and pruned. */
const DRAFT_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

/**
 * On-disk draft envelope. `ts` is the last-write epoch ms; `v` is a
 * schema tag so a future shape change can be detected. Legacy entries
 * were bare markdown strings (no envelope) — `parseEntry` treats those
 * as fresh so existing drafts survive the upgrade.
 */
interface DraftEnvelope {
  readonly v: 1;
  readonly text: string;
  readonly ts: number;
}

function storage(): Storage | null {
  try {
    if (typeof localStorage !== 'undefined') return localStorage;
  } catch {
    /* access can throw in sandboxed/SSR contexts */
  }
  return null;
}

/** Current epoch ms, guarded — `Date.now` is always available on web. */
function nowMs(): number {
  return Date.now();
}

/**
 * Decode a stored value into `{ text, ts }`. Handles the legacy
 * bare-string format (pre-TTL) by treating it as a just-written draft
 * so upgrading users don't lose in-progress messages.
 */
function parseEntry(raw: string): DraftEnvelope | null {
  if (raw.length === 0) return null;
  if (raw[0] === '{') {
    try {
      const parsed = JSON.parse(raw) as Partial<DraftEnvelope>;
      if (
        parsed !== null &&
        typeof parsed === 'object' &&
        typeof parsed.text === 'string' &&
        typeof parsed.ts === 'number'
      ) {
        return { v: 1, text: parsed.text, ts: parsed.ts };
      }
    } catch {
      /* fall through to legacy handling */
    }
  }
  // Legacy raw-string draft — adopt it as fresh.
  return { v: 1, text: raw, ts: nowMs() };
}

/**
 * Remove every draft whose timestamp is older than `DRAFT_MAX_AGE_MS`.
 * Cheap and bounded (composer drafts are few), so it's safe to run on
 * each load/save rather than scheduling a sweeper.
 */
function pruneExpired(s: Storage): void {
  const cutoff = nowMs() - DRAFT_MAX_AGE_MS;
  try {
    const stale: string[] = [];
    for (let i = 0; i < s.length; i++) {
      const key = s.key(i);
      if (key === null || !key.startsWith(PREFIX)) continue;
      const entry = parseEntry(s.getItem(key) ?? '');
      if (entry === null || entry.ts < cutoff) {
        stale.push(key);
      }
    }
    for (const key of stale) s.removeItem(key);
  } catch {
    /* best-effort — never throw into the render path */
  }
}

/** Pure, render-safe read: the draft text for `key`, or `''` when none / expired
 *  / unavailable. Unlike [`loadComposerDraft`] it NEVER mutates storage (no
 *  expiry `removeItem`, no full-keyspace `pruneExpired` sweep), so it is safe to
 *  call in a render hot path — e.g. once per chat-list row per render, where the
 *  "once per composer mount" prune assumption does NOT hold. Expired entries read
 *  as `''` and are reclaimed later by the composer's own `loadComposerDraft`. */
export function peekComposerDraft(key: string): string {
  if (key.length === 0) return '';
  const s = storage();
  if (s === null) return '';
  try {
    const entry = parseEntry(s.getItem(PREFIX + key) ?? '');
    if (entry === null || entry.ts < nowMs() - DRAFT_MAX_AGE_MS) return '';
    return entry.text;
  } catch {
    return '';
  }
}

/** Read the saved draft for `key`, or `''` when none / stale / unavailable. */
export function loadComposerDraft(key: string): string {
  if (key.length === 0) return '';
  const s = storage();
  if (s === null) return '';
  try {
    const entry = parseEntry(s.getItem(PREFIX + key) ?? '');
    if (entry === null) return '';
    if (entry.ts < nowMs() - DRAFT_MAX_AGE_MS) {
      s.removeItem(PREFIX + key);
      return '';
    }
    // Opportunistically prune other conversations' expired drafts while
    // we're here (once per composer mount, so cost is negligible).
    pruneExpired(s);
    return entry.text;
  } catch {
    return '';
  }
}

/** Persist (non-empty) `text` for `key`; empty text clears the entry. */
export function saveComposerDraft(key: string, text: string): void {
  if (key.length === 0) return;
  const s = storage();
  if (s === null) return;
  try {
    if (text.length === 0) {
      s.removeItem(PREFIX + key);
    } else {
      const envelope: DraftEnvelope = { v: 1, text, ts: nowMs() };
      s.setItem(PREFIX + key, JSON.stringify(envelope));
    }
  } catch {
    /* quota / disabled — drafts are best-effort */
  }
}

/** Remove the saved draft for `key` (on send / explicit clear). */
export function clearComposerDraft(key: string): void {
  saveComposerDraft(key, '');
}
